import { ApiError, configureClient, type Effect, setState } from '@almanac/client';
import { dayKey } from '@almanac/shared';
import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { App } from './App';
import { ErrorBoundary } from './components/ErrorBoundary';
import { PICTURE_SIZES } from './components/game/pictureSizes';
import { dropOldShares } from './lib/device';
import { reportCrash, startMonitoring } from './lib/monitoring';
import { isPainted, paintedSignedOut } from './lib/painted';
import { reloadForNewVersion } from './lib/reload';
import { buzz, sfx, unlockAudioOnFirstTap } from './lib/sound';
import { watchSystemTheme } from './lib/theme';
import { prefetchStart, preloadPage } from './pages/lazy';
import './styles.css';

// Every page opens at the top (App scrolls there on each move). Left to the browser, a step back through history also
// brings back the old entry's scroll: the results of a match begun from a scrolled lobby opened half-way down.
window.history.scrollRestoration = 'manual';

const SOUNDS: Partial<Record<Effect, () => void>> = {
  lock: sfx.lock,
  question: sfx.question,
  correct: sfx.correct,
  wrong: sfx.wrong,
  timeUp: sfx.timeUp,
  powerup: sfx.powerup,
  eliminated: sfx.eliminated,
  knockedOut: sfx.knockedOut,
  achievement: sfx.achievement,
  invite: sfx.invite,
  round: sfx.go,
  leadTaken: () => sfx.lead(true),
  leadLost: () => sfx.lead(false),
};

/** Felt, not heard: a tap for your pick, a double pulse for right, a thud for wrong, a long one for being knocked out. */
const HAPTICS: Partial<Record<Effect, number | number[]>> = {
  lock: 12,
  correct: [18, 50, 18],
  wrong: 90,
  timeUp: 60,
  knockedOut: [180, 70, 260],
};

/** Pictures being fetched ahead: held until they're in, so the fetch isn't dropped with an unreferenced image. */
const preloading = new Set<HTMLImageElement>();

// Same-origin API, httpOnly session cookie.
configureClient({
  baseUrl: '',
  // Which client and build: a server that no longer takes this build's protocol says `update-required`, and the tab
  // reloads onto the new one, as for a new build (lib/reload.ts, never twice in a row).
  client: 'web',
  build: document.querySelector<HTMLMetaElement>('meta[name="app-build"]')?.content ?? '',
  onUpdateRequired: () => void reloadForNewVersion(),
  // A tab shown again (a phone unlocked), and the network back: the socket tries again at once.
  onResume: (listener) =>
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') listener();
    }),
  onOnline: (listener) => window.addEventListener('online', listener),
  effect: (name) => {
    SOUNDS[name]?.();
    buzz(HAPTICS[name]);
  },
  // The size the question will show it at (PICTURE_SIZES): the same file the browser will want then.
  preload: ({ src, srcSet }) => {
    const img = new Image();
    img.sizes = PICTURE_SIZES;
    img.srcset = srcSet;
    img.src = src;
    preloading.add(img);
    const done = () => preloading.delete(img);
    img.onload = done;
    img.onerror = done;
  },
});

// Script errors and failed promises outside React's reach. Browser extensions' errors and expected API failures
// (shown to the player already) aren't ours to report.
window.addEventListener('error', (event) => {
  if (event.filename && !event.filename.startsWith(window.location.origin)) return;
  reportCrash('error', event.error ?? event.message);
});
window.addEventListener('unhandledrejection', (event) => {
  if (!(event.reason instanceof ApiError)) reportCrash('rejection', event.reason);
});

startMonitoring();
// public/theme.js put the chosen theme on before the first paint; this keeps up with the device's own while it's the
// choice.
watchSystemTheme();
unlockAudioOnFirstTap();

// Installed or not: without a connection, a page shows "Няма връзка" instead of the browser's error (public/sw.js).
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => void navigator.serviceWorker.register('/sw.js').catch(() => {}));
}

// The landing page the server drew for a visitor without a session (lib/painted.ts): no need to ask who's signed in
// before showing it. (The app still asks, in the background.)
// Any other page of the game opened without a session (the server says so on the root; a challenge link's page has
// the challenge's preview in it too): the same, so the sign-in, or the challenge, shows at once.
const signedOut = paintedSignedOut || document.getElementById('root')?.dataset.signedOut === '1';
if (signedOut) setState({ authChecked: true, me: null });
dropOldShares(dayKey());

const container = document.getElementById('root')!;
const app = (
  <StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </ErrorBoundary>
  </StrictMode>
);
// A page the server drew is hydrated, not drawn again: it stays on screen as it is and becomes interactive (a tap
// that comes before that is replayed). Its code first, and the quiz it shows (pages/lazy.ts → preloadPage). A page
// the browser draws: what it will show is asked for now, beside /api/me rather than after it.
const drawn = isPainted();
if (!drawn) prefetchStart(window.location.pathname, signedOut);
preloadPage(window.location.pathname, drawn && signedOut)
  .catch(() => {})
  .then(() => (drawn ? hydrateRoot(container, app) : createRoot(container).render(app)));
